import { useEffect, useMemo, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import TaskDrawer from "../../components/TaskDrawer";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import { myPastTasks, myTaskGroups } from "../../api/myTaskService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate, formatDateTime, analyzeTaskTiming, serialNumber, initials } from "../../utils/formatters";
import { fetchAndDownload } from "../../utils/exportCsv";
import { showApiError, showToast } from "../../utils/toast";

export default function MyHistoryPage() {
  const [openTask, setOpenTask] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "completed" | "cancelled"
  const [roleFilter, setRoleFilter] = useState(""); // "" (both) | "assignee" | "reviewer"
  const [taskGroupId, setTaskGroupId] = useState("");
  const [completedFrom, setCompletedFrom] = useState("");
  const [completedTo, setCompletedTo] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, roleFilter, taskGroupId, completedFrom, completedTo, perPage]);

  const tasks = useApi(
    () => {
      const params = { page, per_page: perPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;
      if (taskGroupId) params.task_group_id = taskGroupId;
      if (completedFrom) params.completed_from = completedFrom;
      if (completedTo) params.completed_to = completedTo;
      return myPastTasks(params);
    },
    [page, perPage, debouncedSearch, statusFilter, roleFilter, taskGroupId, completedFrom, completedTo],
  );
  const groups = useApi(() => myTaskGroups());

  const rows = paginatedRows(tasks.data);
  const meta = paginatedMeta(tasks.data);
  const groupRows = paginatedRows(groups.data);
  const groupOptions = useMemo(
    () => groupRows.map((g) => ({ id: g.id, label: g.name })),
    [groupRows],
  );
  const verified = rows.filter((t) => t.is_verified).length;

  const filterActive =
    !!debouncedSearch || !!statusFilter || !!roleFilter || !!taskGroupId || !!completedFrom || !!completedTo;
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setRoleFilter("");
    setTaskGroupId("");
    setCompletedFrom("");
    setCompletedTo("");
  };

  const exportHistory = async () => {
    setExporting(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (roleFilter) params.role = roleFilter;
      if (taskGroupId) params.task_group_id = taskGroupId;
      if (completedFrom) params.completed_from = completedFrom;
      if (completedTo) params.completed_to = completedTo;
      await fetchAndDownload("/my/tasks/history/export", params, "my_history");
      showToast("success", "Export downloaded");
    } catch (err) {
      showApiError(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">History</h1>
          <div className="page-sub">{meta?.total ?? rows.length} tasks · {verified} verified on this page</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={exportHistory} disabled={exporting}>
            <Icon name="download" size={14} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{ marginBottom: 12, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Icon
              name="search"
              size={14}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
            />
            <input
              className="input"
              style={{ paddingLeft: 34 }}
              placeholder="Search task or business…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="btn btn-ghost" onClick={clearFilters} disabled={!filterActive}>
            <Icon name="x" size={13} /> Clear
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
          <FilterField label="Status">
            <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </FilterField>
          <FilterField label="My role">
            <select className="select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Any</option>
              <option value="assignee">Assigned to me</option>
              <option value="reviewer">Reviewing</option>
            </select>
          </FilterField>
          <FilterField label="Task group">
            <SearchableSelect
              value={taskGroupId}
              onChange={(v) => setTaskGroupId(v ?? "")}
              options={groupOptions}
              placeholder="All groups"
              searchPlaceholder="Search groups…"
              width="100%"
            />
          </FilterField>
          <FilterField label="Completed from">
            <input type="date" className="input" value={completedFrom} onChange={(e) => setCompletedFrom(e.target.value)} />
          </FilterField>
          <FilterField label="Completed to">
            <input type="date" className="input" value={completedTo} onChange={(e) => setCompletedTo(e.target.value)} />
          </FilterField>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {tasks.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="clock"
              title={filterActive ? "No tasks match the filter" : "No history yet"}
              hint={filterActive ? "Try clearing some filters." : "Completed and cancelled tasks will appear here."}
              action={filterActive ? (
                <button className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
              ) : null}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Task</th>
                  <th>Business</th>
                  <th>Assignee</th>
                  <th>Reviewer</th>
                  <th>Verified</th>
                  <th className="t-num">Due</th>
                  <th className="t-num">Completed</th>
                  <th>Timing</th>
                  <th style={{ width: 70 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t, i) => {
                  const a = analyzeTaskTiming(t);
                  const cls = {
                    success: "badge-success",
                    warning: "badge-warning",
                    danger: "badge-danger",
                    info: "badge-info",
                    neutral: "badge-neutral",
                  }[a.tone] || "badge-neutral";
                  return (
                    <tr key={t.id} className="clickable" onClick={() => setOpenTask(t.id)}>
                      <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                      <td className="t-primary">{t.task_master?.name}</td>
                      <td className="t-muted">{t.business?.business_name}</td>
                      <td>
                        {t.employee ? (
                          <div className="row gap-sm">
                            <div className="avatar avatar-sm">{initials(t.employee.name)}</div>
                            <span className="t-muted text-xs">{t.employee.name?.split(" ")[0]}</span>
                          </div>
                        ) : (
                          <span className="badge badge-warning">Unassigned</span>
                        )}
                      </td>
                      <td>
                        {t.reviewer ? (
                          <div className="row gap-sm">
                            <div className="avatar avatar-sm">{initials(t.reviewer.name)}</div>
                            <span className="t-muted text-xs">{t.reviewer.name?.split(" ")[0]}</span>
                          </div>
                        ) : (
                          <span className="t-muted">—</span>
                        )}
                      </td>
                      <td>
                        {t.is_verified ? (
                          <span className="badge badge-success"><Icon name="check" size={10} /> Verified</span>
                        ) : (
                          <span className="badge badge-warning">Pending</span>
                        )}
                      </td>
                      <td className="t-num t-muted">{formatDate(t.due_date)}</td>
                      <td className="t-num t-muted">
                        {t.completed_at
                          ? formatDateTime(t.completed_at)
                          : formatDate(t.completion_date || t.updated_at)}
                      </td>
                      <td><span className={`badge ${cls}`}>{a.label}</span></td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <button
                          className="icon-btn"
                          title="View"
                          onClick={() => setOpenTask(t.id)}
                        >
                          <Icon name="eye" size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={tasks.loading}
          />
        </div>
      </div>

      <TaskDrawer
        taskId={openTask}
        role="employee"
        onClose={() => setOpenTask(null)}
      />
    </>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}
